import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock, Mail, Pencil, Plus, Unlink } from 'lucide-react';
import { Button } from '../../../shared/ui/Button';
import { Badge } from '../../../shared/ui/Badge';
import { Input } from '../../../shared/ui/Input';
import { Modal, ModalActions } from '../../../shared/ui/Modal';
import { OtpInput } from '../../../shared/ui/OtpInput';
import { ConfirmModal } from '../../../shared/ui/ConfirmModal';
import { Skeleton } from '../../../shared/ui/Skeleton';
import { toast } from '../../../shared/ui/toast';
import { ApiError } from '../../../shared/api/http';
import { formatRemaining } from '../../../shared/lib/format';
import { useNow } from '../../../shared/lib/useNow';
import { emitOnboardingEvent } from '../../onboarding/intents';
import {
  useAddPrimaryEmail,
  usePrimaryEmail,
  useRemovePrimaryEmail,
  useResendPrimaryOtp,
  useVerifyPrimaryEmail,
} from '../api';

type Step = 'email' | 'otp';

const OTP_EXPIRED_CODES = new Set([-1_300_601_410, -1_300_601_429]);
// Клиентская проверка формата email — не отправляем заведомо некорректный адрес на бэкенд (§2).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Карточка основного (личного) адреса переадресации: привязка + OTP-подтверждение + отвязка.
 * Состояния: пусто → добавить; pending → ввести код / выслать снова; verified → сменить / отвязать.
 */
export function ForwardingPrimaryCard({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const now = useNow();

  const primaryQuery = usePrimaryEmail(enabled);
  const add = useAddPrimaryEmail();
  const verify = useVerifyPrimaryEmail();
  const resend = useResendPrimaryOtp();
  const remove = useRemovePrimaryEmail();

  const primary = primaryQuery.data ?? null;

  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [unbindOpen, setUnbindOpen] = useState(false);

  const openAdd = () => {
    setEmail(primary?.email ?? '');
    setEmailError(null);
    setStep('email');
    setModalOpen(true);
  };

  const openOtp = () => {
    setCode('');
    setCodeError(null);
    setStep('otp');
    setModalOpen(true);
  };

  const submitEmail = () => {
    setEmailError(null);
    const value = email.trim();
    // Валидируем формат до запроса: некорректный email не уходит на бэкенд, показываем
    // человекочитаемую ошибку вместо «One or more validation errors occurred.» (§2).
    if (!EMAIL_RE.test(value)) {
      setEmailError(t('forwarding.otp1.invalid'));
      return;
    }
    add.mutate(value, {
      onSuccess: () => {
        setCode('');
        setCodeError(null);
        setStep('otp');
      },
      onError: (e) => {
        // validationError с бэкенда (напр. EMAIL_INVALID) → человекочитаемый текст.
        if (e instanceof ApiError) {
          setEmailError(e.type === 'validationError' ? t('forwarding.otp1.invalid') : e.message);
        } else {
          setEmailError(t('forwarding.otp1.invalid'));
        }
      },
    });
  };

  const submitOtp = () => {
    setCodeError(null);
    verify.mutate(code.trim(), {
      onSuccess: () => {
        setModalOpen(false);
        toast.success(t('forwarding.toasts.confirmed', { email: email || primary?.email }));
        emitOnboardingEvent('forwarding.confirmed');
      },
      onError: (e) => {
        if (e instanceof ApiError && OTP_EXPIRED_CODES.has(e.eventCode ?? 0)) {
          setCodeError(t('forwarding.otp2.expired'));
        } else {
          setCodeError(t('forwarding.otp2.invalid'));
        }
      },
    });
  };

  const onResend = () => {
    resend.mutate(undefined, {
      onSuccess: () => toast.success(t('forwarding.toasts.otpSent')),
      onError: () => toast.error(t('common.error')),
    });
  };

  const onUnbind = () => {
    remove.mutate(undefined, {
      onSuccess: () => {
        setUnbindOpen(false);
        toast.success(t('forwarding.toasts.unbound'));
      },
    });
  };

  const otpRemaining = primary?.otpExpiresAt ? formatRemaining(primary.otpExpiresAt, now) : null;

  return (
    <section className="rounded-2xl bg-surface p-5 shadow-card">
      <h2 className="font-semibold text-fg">{t('forwarding.primaryTitle')}</h2>

      {primaryQuery.isLoading ? (
        <Skeleton className="mt-3 h-16 w-full rounded-xl" />
      ) : primary === null ? (
        <>
          <p className="mt-1 text-sm text-fg-muted">{t('forwarding.primaryEmptyHint')}</p>
          <Button className="mt-3" onClick={openAdd}>
            <Plus className="h-4 w-4" /> {t('forwarding.addPrimary').replace('+ ', '')}
          </Button>
        </>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent-text">
              <Mail className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-fg">{primary.email}</p>
              <p className="text-xs text-fg-muted">
                {t('forwarding.primarySetHint', { email: primary.email })}
              </p>
            </div>
            {primary.status === 'verified' ? (
              <Badge tone="active" dot>
                <CheckCircle2 className="h-3.5 w-3.5" /> {t('status.active')}
              </Badge>
            ) : (
              <Badge tone="warning" dot>
                <Clock className="h-3.5 w-3.5" /> {t('forwarding.pending')}
              </Badge>
            )}
          </div>

          {/* Замечание РТК W4/M6: две полноценные кнопки с иконками, а не текстовые ссылки. */}
          <div className="flex flex-wrap gap-2">
            {primary.status === 'pending' && (
              <>
                <Button size="sm" onClick={openOtp}>
                  {t('forwarding.enterCode')}
                </Button>
                <Button size="sm" variant="secondary" onClick={onResend} loading={resend.isPending}>
                  {t('forwarding.otp2.resend')}
                </Button>
              </>
            )}
            {primary.status === 'verified' && (
              <Button size="sm" onClick={openAdd}>
                <Pencil className="h-4 w-4" /> {t('forwarding.changePrimary')}
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setUnbindOpen(true);
              }}
            >
              <Unlink className="h-4 w-4" /> {t('forwarding.unbindPrimary')}
            </Button>
          </div>
        </div>
      )}

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={step === 'email' ? t('forwarding.otp1.title') : t('forwarding.otp2.title')}
        description={
          step === 'email'
            ? t('forwarding.otp1.subtitle')
            : t('forwarding.otp2.subtitle', { email: email || primary?.email })
        }
      >
        {step === 'email' ? (
          <>
            <Input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(null);
              }}
              onClear={() => {
                setEmail('');
              }}
              placeholder={t('forwarding.otp1.emailPlaceholder')}
              error={emailError}
            />
            <ModalActions>
              <Button onClick={submitEmail} loading={add.isPending} disabled={email.trim() === ''}>
                {t('forwarding.otp1.submit')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </ModalActions>
          </>
        ) : (
          <>
            <OtpInput
              value={code}
              onChange={setCode}
              autoFocus
              error={codeError}
              aria-label={t('forwarding.otp2.title')}
            />
            {otpRemaining && (
              <p className="mt-3 text-xs text-fg-muted">
                {t('forwarding.otp2.expiresIn', { time: otpRemaining })}
              </p>
            )}
            <ModalActions>
              <Button onClick={submitOtp} loading={verify.isPending} disabled={code.length !== 6}>
                {t('forwarding.otp2.submit')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </ModalActions>
            <button
              type="button"
              onClick={onResend}
              disabled={resend.isPending}
              className="mt-4 block w-full text-center text-sm font-medium text-brand hover:underline disabled:text-fg-muted disabled:no-underline sm:w-auto sm:text-left"
            >
              {t('forwarding.otp2.resend')}
            </button>
          </>
        )}
      </Modal>

      <ConfirmModal
        open={unbindOpen}
        onOpenChange={setUnbindOpen}
        title={t('forwarding.unbind.title', { email: primary?.email ?? '' })}
        description={t('forwarding.unbind.subtitle')}
        confirmLabel={t('forwarding.unbind.confirm')}
        onConfirm={onUnbind}
        loading={remove.isPending}
      />
    </section>
  );
}
